/**
 * Live reads from Dexie — the bridge between React and `lib/` (W-1).
 *
 * Components use these; they never import Dexie or the backend client themselves. Every
 * query here is local, so every render is instant and works offline.
 */

import { useLiveQuery } from "dexie-react-hooks";
import { useContext, useEffect, useMemo, useState } from "react";

import {
  type CarryContext,
  type BudgetStanding,
  budgetStandings,
  resolveRolloverMode,
  sumBaseBudgets,
} from "@/domain/budget";
import { startCategoryId } from "@/domain/default_category";
import { type Cents, ZERO, cents, sumCents } from "@/domain/money";
import { type Period, daysRemaining, periodBounds, periodLabel, previousPeriod } from "@/domain/period";
import { isSavingsCategory } from "@/domain/category_kind";
import { salaryInForce } from "@/domain/salary";
import { allocationGap, expenseCeiling, savingsCommitted } from "@/domain/savings";
import { type Comparison, comparison, periodTotals } from "@/domain/totals";
import { db as personalDb } from "@/lib/db";
import { type FamilySyncStatus, countPending, getFamilySyncStatus, subscribeFamilySync, getSyncStatus, subscribeSync } from "@/lib/sync";
import type { Budget, Category, LocalExpense, Profile, Salary } from "@/lib/types";

import { defaultProfile } from "@/lib/profile_defaults";

import { LedgerContext } from "./use_ledger";
export { LedgerProvider, useLedger } from "./use_ledger";

/** How often "now" is re-read. Hourly is enough to catch a period boundary. */
const NOW_TICK_MS = 3_600_000;

export interface EffectiveProfile {
  currency: string;
  timezone: string;
  cycleStartDay: number;
  rolloverMode: Profile["rolloverMode"];
  onboardedAt: string | null;
  /** The category Capture opens on, or null for "the last one used". May name a retired one. */
  defaultCategoryId: string | null;
  /**
   * False until the profile row has actually loaded. A missing row is NOT loaded: onboarding and the salary
   * gate treat `loaded` as "this device has the account's real settings", and writing defaults before the
   * first pull would overwrite them by last write wins (INV-2).
   */
  loaded: boolean;
  /** True once the query has answered, row or no row. Only for reads that may safely use the defaults. */
  resolved: boolean;
  gridOrder?: string[] | null;
}

/**
 * The profile, with defaults applied.
 *
 * Defaults rather than a loading gate: a brand-new user has no profile row until
 * the first sync lands, and blocking the numpad on a network round-trip would
 * break the one promise the app makes (INV-4).
 */
export function useProfile(enabled = true): EffectiveProfile {
  const db = useContext(LedgerContext)?.db ?? personalDb;
  const row = useLiveQuery(async () => enabled ? (await db.profile.toCollection().first()) ?? null : undefined, [db, enabled]);
  const defaults = defaultProfile("", "");
  return useMemo(
    () => ({
      currency: row?.currency ?? defaults.currency,
      timezone:
        row?.timezone ?? defaults.timezone,
      cycleStartDay: row?.cycleStartDay ?? defaults.cycleStartDay,
      rolloverMode: row?.rolloverMode ?? defaults.rolloverMode,
      onboardedAt: row?.onboardedAt ?? null,
      defaultCategoryId: row?.defaultCategoryId ?? null,
      gridOrder: row?.gridOrder ?? null,
      loaded: row !== undefined && row !== null,
      resolved: row !== undefined,
    }),
    [row, defaults.currency, defaults.timezone, defaults.cycleStartDay, defaults.rolloverMode],
  );
}

/**
 * Categories you can log an expense against, in the user's order.
 *
 * Archived ones are excluded, and so are **savings** ones: nothing is ever
 * logged against a savings category, so offering one on the numpad or in an edit
 * picker would let a user file spending somewhere it can never be counted
 * (scope expansion 2026-07-31).
 *
 * This is deliberately not the list the budget standings walk — those need both
 * kinds, because a savings budget is what the ceiling subtracts. Use
 * {@link useActiveCategories} for that.
 */
export function useCategories(): Category[] {
  return useCategoriesWithLoad().rows;
}

/** Capture's categories and the first-read signal, from one live query (W-1). */
export function useCategoriesWithLoad(): { rows: Category[]; loaded: boolean } {
  const rows = useCategoryRows();
  const categories = useMemo(() => (rows ?? []).filter((c) => !isSavingsCategory(c)), [rows]);
  return { rows: categories, loaded: rows !== undefined };
}

/** The savings categories — what `/savings` lists and what the ceiling subtracts. */
export function useSavingsCategories(): Category[] {
  const rows = useActiveCategories();
  return useMemo(() => rows.filter((c) => isSavingsCategory(c)), [rows]);
}

/**
 * The ids of every savings category, **archived ones included**.
 *
 * Deliberately not `useSavingsCategories().map(...)`. Archiving is orthogonal to
 * the partition: a category that is archived is still savings, and its expenses
 * are still not spending. Reading the active list here would mean "mark as
 * savings, then archive it" — an ordinary tidy-up — silently re-admitted the
 * whole of that category's history into every total.
 */
export function useSavingsCategoryIds(): Set<string> {
  const rows = useAllCategories();
  return useMemo(
    () => new Set(rows.filter((c) => isSavingsCategory(c)).map((c) => c.id)),
    [rows],
  );
}

/**
 * Every live, unarchived category — savings and expenditure alike.
 *
 * The standings hooks read this rather than {@link useCategories}, because the
 * expense ceiling is `salary − Σ savings budgets`: dropping savings categories
 * before the sum would silently make the ceiling the whole salary.
 */
function useCategoryRows(): Category[] | undefined {
  const db = useContext(LedgerContext)?.db ?? personalDb;
  return useLiveQuery(
    () =>
      db.categories
        .orderBy("sortOrder")
        .filter((c) => c.deletedAt === null && c.archivedAt === null)
        .toArray(),
    [db],
  );
}

export function useActiveCategories(): Category[] { return useCategoryRows() ?? []; }

/** Every category including archived and savings — for Settings, and for labelling old rows. */
export function useAllCategories(): Category[] {
  return useAllCategoryRows() ?? [];
}

/** All category rows, retaining the first-read signal for historical insights. */
function useAllCategoryRows(): Category[] | undefined {
  const db = useContext(LedgerContext)?.db ?? personalDb;
  return useLiveQuery(
    () => db.categories.orderBy("sortOrder").filter((c) => c.deletedAt === null).toArray(),
    [db],
  );
}

/**
 * Every live salary version, and whether the table has actually been read.
 *
 * `loaded` is not decoration. The salary gate refuses to let the app start
 * without a salary, so it has to be able to tell "this account has none" from
 * "the query has not come back yet" — and `rows ?? []` collapses exactly those
 * two. Getting it wrong put a full-screen form in front of the numpad on a cold
 * offline boot, which is the one thing INV-4 forbids. `useProfile` carries the
 * same flag for the same reason.
 */
export function useSalaries(): { rows: Salary[]; loaded: boolean } {
  const db = useContext(LedgerContext)?.db ?? personalDb;
  const rows = useLiveQuery(() => db.salaries.filter((s) => s.deletedAt === null).toArray(), [db]);
  return useMemo(() => ({ rows: rows ?? [], loaded: rows !== undefined }), [rows]);
}

/**
 * Every live expense.
 *
 * Loaded whole because rollover has to walk prior periods, and a personal
 * ledger is small — five years of daily logging is under 2,000 rows. If that
 * ever stops being true, bound this by the carry window rather than paginating
 * the home screen.
 */
export function useAllExpenses(): LocalExpense[] {
  return useExpenseRows() ?? [];
}

/**
 * Every live expense that counts as **spending**.
 *
 * Nothing can be logged against a savings category any more, but rows logged
 * against one *before* it was marked savings still exist. They are excluded
 * here, so Trends cannot show a total that Horizon and the Ledger do not — two
 * screens disagreeing about the same ledger is worse than either figure being
 * arguable on its own.
 */
export function useSpendingExpenses(): LocalExpense[] {
  const expenses = useAllExpenses();
  const savingsIds = useSavingsCategoryIds();

  return useMemo(
    () => expenses.filter((e) => !savingsIds.has(e.categoryId)),
    [expenses, savingsIds],
  );
}

function useExpenseRows() {
  const db = useContext(LedgerContext)?.db ?? personalDb;
  return useLiveQuery(() => db.expenses.filter((e) => e.deletedAt === null).toArray(), [db]);
}
function useBudgetRows() {
  const db = useContext(LedgerContext)?.db ?? personalDb;
  return useLiveQuery(() => db.budgets.filter((b) => b.deletedAt === null).toArray(), [db]);
}
export function useBudgets() { return useBudgetRows() ?? []; }

export function useRecurringRules() {
  return useRuleRows() ?? [];
}

/** Repeating rules, retaining the first-read signal. */
function useRuleRows() {
  const db = useContext(LedgerContext)?.db ?? personalDb;
  return useLiveQuery(
    () => db.recurringRules.filter((r) => r.deletedAt === null).toArray(),
    [db],
  );
}

/** Wait for all four local reads before making a historical claim. */
export function useInsightHistory() {
  const categories = useAllCategoryRows();
  const expenses = useExpenseRows();
  const budgets = useBudgetRows();
  const recurringRules = useRuleRows();
  return useMemo(() => ({
    categories: categories ?? [], expenses: expenses ?? [], budgets: budgets ?? [], recurringRules: recurringRules ?? [],
    loaded: categories !== undefined && expenses !== undefined && budgets !== undefined && recurringRules !== undefined,
  }), [categories, expenses, budgets, recurringRules]);
}

/**
 * The current period, recomputed when the profile or the day changes.
 *
 * No label: `periodLabel` belongs to whichever period is being shown, which is
 * not always this one, so it is computed in `computeStanding` instead.
 */
/**
 * The current instant, re-read hourly, so a period or window boundary crossed
 * while the app sits open does not leave a stale figure on screen.
 *
 * Each caller gets its **own** ticker, phased from when it mounted — this is one
 * implementation of the behaviour, not one shared clock. That is fine for its
 * callers, which are one per route; anything needing two consumers to agree on
 * the same instant must be handed a `Date`, not call this twice.
 */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), NOW_TICK_MS);
    return () => clearInterval(id);
  }, []);

  return now;
}

export function useCurrentPeriod(): { period: Period; daysLeft: number } {
  const profile = useProfile();
  const now = useNow();

  return useMemo(() => {
    const period = periodBounds(now, profile.cycleStartDay, profile.timezone);
    return { period, daysLeft: daysRemaining(period, now, profile.timezone) };
  }, [now, profile.cycleStartDay, profile.timezone]);
}

export interface CategoryStanding extends BudgetStanding {
  category: Category;
}

/**
 * Everything about a period that is true of the period itself.
 *
 * Nothing here depends on the current instant, which is what lets the Ledger
 * point it at a closed period and get honest numbers back.
 */
export interface PeriodStanding {
  period: Period;
  label: string;
  /** Total spend this period. Savings categories contribute nothing. */
  spent: Cents;
  /**
   * What there is to spend: `salary − Σ savings budgets`.
   *
   * Falls back to the sum of the expenditure budgets when no salary is set,
   * which is what every account looked like before the ceiling existed. Null
   * when neither is knowable.
   */
  totalBudget: Cents | null;
  /**
   * `salary − Σ savings budgets`, or null when no salary is in force.
   *
   * Deliberately separate from {@link totalBudget}, which falls back to the sum
   * of the budgets. A **negative** value means savings alone come to more than
   * the salary, and that is not inferable from `totalBudget` — the fallback can
   * never be negative, so the two states would be indistinguishable in exactly
   * the case worth naming.
   */
  ceiling: Cents | null;
  /** totalBudget − spent. Null when there is no ceiling. */
  remaining: Cents | null;
  isOver: boolean;
  /** 0..∞ — drives the water level. */
  ratio: number;
  /** **Expenditure** categories only. Savings never render as spending. */
  standings: CategoryStanding[];
  /** The savings categories and their amounts — what `/savings` lists. */
  savingsStandings: CategoryStanding[];
  /** Σ savings budgets: what comes off the salary before anything is spent. */
  savingsCommitted: Cents;
  /**
   * Ceiling − Σ expenditure budgets. Positive means headroom not yet allocated,
   * negative means the budgets promise more than the salary covers. Null when
   * there is nothing to reconcile.
   */
  allocationGap: Cents | null;
  currency: string;
  hasAnyBudget: boolean;
}

/**
 * A period standing plus the two figures that only mean anything *now*.
 *
 * `daysLeft` and `comparison` are measured against the current instant, so they
 * are nonsense for a period that has already closed. They live here rather than
 * on `PeriodStanding` so a past period cannot be handed a countdown to a
 * deadline that passed — the Ledger simply has no such field to render.
 */
export interface PeriodView extends PeriodStanding {
  daysLeft: number;
  comparison: Comparison;
}

/**
 * The standing for **any** period — the Ledger points this at whichever period
 * is selected, which is the whole of past-period browsing (SPEC.md §7).
 *
 * Every boundary question still routes through `periodBounds()` upstream; this
 * hook only reads Dexie and delegates the maths to `src/domain/` (INV-2, C-6).
 */
export function usePeriodStanding(period: Period): PeriodStanding {
  const profile = useProfile();
  const categories = useAllCategories();
  const expenses = useSpendingExpenses();
  const budgets = useBudgets();
  const { rows: salaries } = useSalaries();

  return useMemo(
    () => computeStanding(period, categories, expenses, budgets, salaries, profile),
    [categories, expenses, budgets, salaries, period, profile],
  );
}

/**
 * The standing for a list of periods — what the Ledger's period picker shows.
 *
 * Shares `computeStanding` with `usePeriodStanding` rather than approximating,
 * because a total in the picker that disagrees with the total on the screen it
 * navigates to is worse than no total at all.
 *
 * Rollover's backward walk used to make this superlinear in the number of
 * periods — ~1s at 60 periods with rollover on (ROADMAP 8.8). It now runs
 * **one** carry walk per category for the whole list (`budgetStandings`), so the
 * cost is the walk plus a pass per period rather than a walk per period.
 * `MAX_PERIOD_WALK` is a corruption backstop, not a performance bound, and was
 * deliberately left alone.
 */
export function usePeriodOptions(periods: readonly Period[]): PeriodStanding[] {
  const profile = useProfile();
  const categories = useAllCategories();
  const expenses = useSpendingExpenses();
  const budgets = useBudgets();
  const { rows: salaries } = useSalaries();

  return useMemo(
    () => computeStandings(periods, categories, expenses, budgets, salaries, profile),
    [periods, categories, expenses, budgets, salaries, profile],
  );
}

/**
 * The one implementation of "how are these periods standing".
 *
 * Pure, and shared by every caller, so the Ledger's header, its gauge, the
 * period picker and the analytics view can never disagree about the same
 * period's numbers.
 *
 * Two things are hoisted out of the per-period loop, and both matter at 60
 * periods: each category's expenses and budgets are filtered **once** rather
 * than once per period, and the rollover carry is walked once for the whole list
 * (ROADMAP 8.8).
 */
export function computeStandings(
  periods: readonly Period[],
  categories: Category[],
  expenses: LocalExpense[],
  budgets: Budget[],
  salaries: Salary[],
  profile: EffectiveProfile,
): PeriodStanding[] {
  const savingsIds = new Set(categories.filter(isSavingsCategory).map((c) => c.id));
  expenses = expenses.filter((e) => !savingsIds.has(e.categoryId));
  const contexts = categories.filter((c) => c.archivedAt === null && c.deletedAt === null).map((category) => ({
    category,
    ctx: {
      budgets: budgets.filter((b) => b.categoryId === category.id),
      expenses: expenses.filter((e) => e.categoryId === category.id),
      cycleStartDay: profile.cycleStartDay,
      timeZone: profile.timezone,
      // Rollover is meaningless for a savings category and actively harmful:
      // nothing is ever logged against one, so "carry surplus" would roll the
      // entire amount into the next period and compound, subtracting an
      // ever-growing figure from the salary until the ceiling vanished. A
      // savings budget is its base amount, every period.
      mode: isSavingsCategory(category)
        ? "none"
        : resolveRolloverMode(profile.rolloverMode, category.rolloverOverride),
    } satisfies CarryContext,
  }));
  const walked = contexts.map(({ category, ctx }) => ({
    category,
    standings: budgetStandings(ctx, periods),
  }));

  return periods.map((period) => {
    const at = period.start.getTime();
    const categoryStandings = walked.map(({ category, standings }) => {
      const standing = standings.get(at);
      // `budgetStandings` sets one per requested period, so a miss here means a
      // period was silently dropped — an empty category list rendered as fact.
      if (!standing) {
        throw new Error(`No standing computed for ${category.name} in the period starting ${period.start.toISOString()}`);
      }
      return { category, ...standing };
    });
    return assembleStanding(period, categoryStandings, expenses, salaries, profile);
  });
}

/** The standing for exactly one period. */
function computeStanding(
  period: Period,
  categories: Category[],
  expenses: LocalExpense[],
  budgets: Budget[],
  salaries: Salary[],
  profile: EffectiveProfile,
): PeriodStanding {
  const standing = computeStandings([period], categories, expenses, budgets, salaries, profile)[0];
  if (!standing) {
    throw new Error(`No standing computed for the period starting ${period.start.toISOString()}`);
  }
  return standing;
}

function assembleStanding(
  period: Period,
  allStandings: CategoryStanding[],
  expenses: LocalExpense[],
  salaries: Salary[],
  profile: EffectiveProfile,
): PeriodStanding {
  const label = periodLabel(period, profile.cycleStartDay, profile.timezone);

  // The partition is the whole change. A savings category's budget is what is
  // put away, not what may be spent, so it must not reach `spent`, `standings`
  // or the sum below.
  const standings = allStandings.filter((s) => !isSavingsCategory(s.category));
  const savingsStandings = allStandings.filter((s) => isSavingsCategory(s.category));

  const savingsIds = new Set(savingsStandings.map((s) => s.category.id));
  const totals = periodTotals(
    expenses.filter((e) => !savingsIds.has(e.categoryId)),
    period,
  );

  const budgeted = standings.filter((s) => s.budget !== null);
  const hasAnyBudget = budgeted.length > 0;
  // What there is to spend this period, carry included. This is the figure the
  // headline total falls back to when no salary is in force, and it has always
  // meant the effective budget — flip it to base and a rollover account's
  // pre-salary history would silently change.
  const allocated = hasAnyBudget
    ? cents(budgeted.reduce((sum, s) => sum + (s.budget ?? 0), 0))
    : null;
  // What the budgets *allocate*, carry excluded — and the only figure that may
  // be compared against the ceiling. The ceiling is `salary − Σ savings BASE
  // budgets`, a recurring amount; carry is an accumulating one. Comparing them
  // made the tally flag grow by the whole unspent budget every period while
  // nothing about the allocation had changed.
  const allocatedBase = hasAnyBudget
    ? cents(budgeted.reduce((sum, s) => sum + (s.budget ?? 0) - s.carried, 0))
    : null;

  const committed = savingsCommitted(
    savingsStandings.map((s) => ({ isSavings: true, budget: s.budget })),
  );
  const ceiling = expenseCeiling(salaryInForce(salaries, period, profile.timezone), committed);

  // With no salary set the app behaves exactly as it did before the ceiling
  // existed — the sum of the expenditure budgets. Existing accounts meet the
  // salary gate before they can reach this, but a period viewed *before* the
  // first salary's `effectiveFrom` legitimately has no ceiling, and inventing
  // one would rewrite history the versioning exists to protect.
  const totalBudget = ceiling ?? allocated;
  const remaining = totalBudget === null ? null : cents(totalBudget - totals.total);

  return {
    period,
    label,
    spent: totals.total,
    totalBudget,
    ceiling,
    remaining,
    isOver: remaining !== null && remaining < 0,
    // Without any budget there is no line to be under, so the water sits low
    // rather than pretending to a fullness it cannot know.
    ratio: totalBudget && totalBudget > 0 ? totals.total / totalBudget : 0,
    standings,
    savingsStandings,
    savingsCommitted: committed,
    allocationGap: allocationGap(ceiling, allocatedBase),
    currency: profile.currency,
    hasAnyBudget: hasAnyBudget || ceiling !== null,
  };
}

/**
 * The subset of a standing the tide gauge actually reads.
 *
 * `PeriodStanding` satisfies it structurally, so the Ledger's gauge takes either
 * a single period's standing or the all-time one without branching on scope —
 * which is what keeps "the gauge shows the scope you are looking at" a single
 * code path rather than two that can disagree.
 */
export interface GaugeStanding {
  label: string;
  spent: Cents;
  totalBudget: Cents | null;
  remaining: Cents | null;
  isOver: boolean;
  ratio: number;
  hasAnyBudget: boolean;
  currency: string;
}

/** What the Ledger's All-time scope is called, everywhere it is named. */
export const ALL_TIME_LABEL = "All time";

/**
 * The standing for the whole ledger — the gauge in the All-time scope.
 *
 * `periods` is passed in rather than derived here, exactly as `usePeriodOptions`
 * takes its list: the walk back to the first expense belongs to the Ledger, which
 * already does it for the stepper's floor, and repeating it here would be a
 * second implementation of "how far back does this ledger reach".
 *
 * Spend is every live expense, which is what "all time" means — not the sum of
 * the listed periods, so a row whose date falls outside the walk is still
 * counted rather than silently vanishing from the total (INV-7). The budget is
 * `sumBaseBudgets` per category: the allowance ever granted, unaffected by
 * rollover for the reason stated there.
 */
export function useAllTimeStanding(periods: readonly Period[]): GaugeStanding {
  const profile = useProfile();
  const categories = useCategories();
  const savings = useSavingsCategories();
  const expenses = useSpendingExpenses();
  const budgets = useBudgets();

  return useMemo(() => {
    // Expenses logged against a category *before* it was marked savings still
    // exist. The period standing excludes them, so this must too — an All-time
    // total that counts rows the period totals do not is the two scopes
    // disagreeing about the same ledger.
    const savingsIds = new Set(savings.map((c) => c.id));
    const spent = sumCents(
      expenses.filter((e) => !savingsIds.has(e.categoryId)).map((e) => e.amountCents),
    );

    let budgeted = 0;
    let hasAnyBudget = false;
    for (const category of categories) {
      const total = sumBaseBudgets(
        budgets.filter((b) => b.categoryId === category.id),
        periods,
        profile.timezone,
      );
      if (total === null) continue;
      hasAnyBudget = true;
      budgeted += total;
    }

    const totalBudget = hasAnyBudget ? cents(budgeted) : null;
    const remaining = totalBudget === null ? null : cents(totalBudget - spent);

    return {
      label: ALL_TIME_LABEL,
      spent,
      totalBudget,
      remaining,
      isOver: remaining !== null && remaining < 0,
      ratio: totalBudget && totalBudget > 0 ? spent / totalBudget : 0,
      hasAnyBudget,
      currency: profile.currency,
    };
  }, [categories, savings, expenses, budgets, periods, profile.timezone, profile.currency]);
}

/**
 * Horizon's view: the **current** period, plus the now-relative figures.
 *
 * It takes no period argument, deliberately — Horizon's claim is "where am I in
 * this period", and a closed period's waterline is a static fact rather than a
 * tide (D-3). Having no parameter is what makes that structural instead of a
 * convention someone can forget.
 */
export function usePeriodView(): PeriodView {
  const profile = useProfile();
  const { period, daysLeft } = useCurrentPeriod();
  const standing = usePeriodStanding(period);
  const expenses = useSpendingExpenses();

  return useMemo(
    () => ({
      ...standing,
      daysLeft,
      comparison: comparison(expenses, period, new Date(), profile.cycleStartDay, profile.timezone),
    }),
    [standing, daysLeft, expenses, period, profile.cycleStartDay, profile.timezone],
  );
}

/**
 * The period that just closed, and the one before it — Home's tide report and
 * swell (SPEC.md §9.1).
 *
 * Carries `loaded`, which no other standing hook does, because this one feeds
 * a **once-per-period decision**: read before Dexie answers, an empty ledger
 * looks like a period with nothing to report, and the moment would be judged
 * on nothing. Every row it is computed from comes from the same three queries
 * `loaded` is derived from, so the flag cannot run ahead of the figures.
 */
export interface PeriodEnd {
  closed: PeriodStanding;
  before: PeriodStanding;
  loaded: boolean;
}

export function usePeriodEnd(): PeriodEnd {
  const profile = useProfile();
  const { period } = useCurrentPeriod();
  const categories = useAllCategoryRows();
  const expenses = useExpenseRows();
  const budgets = useBudgetRows();
  const { rows: salaries, loaded: salariesLoaded } = useSalaries();

  const periods = useMemo(() => {
    const closed = previousPeriod(period, profile.cycleStartDay, profile.timezone);
    return [closed, previousPeriod(closed, profile.cycleStartDay, profile.timezone)];
  }, [period, profile.cycleStartDay, profile.timezone]);

  return useMemo(() => {
    const [closed, before] = computeStandings(periods, categories ?? [], expenses ?? [], budgets ?? [], salaries, profile);
    if (!closed || !before) throw new Error("No standing computed for the closed periods");
    return { closed, before, loaded: categories !== undefined && expenses !== undefined && budgets !== undefined && salariesLoaded };
  }, [periods, categories, expenses, budgets, salaries, salariesLoaded, profile]);
}

/** Expenses within a period, newest first. */
export function useExpensesInPeriod(period: Period): LocalExpense[] {
  const db = useContext(LedgerContext)?.db ?? personalDb;
  const from = period.start.toISOString();
  const to = period.end.toISOString();
  const savingsIds = useSavingsCategoryIds();
  const rows = useLiveQuery(
    () =>
      db.expenses
        .where("occurredAt")
        .between(from, to, true, false)
        .reverse()
        .filter((e) => e.deletedAt === null)
        .toArray(),
    [db, from, to],
  );
  return useMemo(
    () => (rows ?? []).filter((e) => !savingsIds.has(e.categoryId)),
    [rows, savingsIds],
  );
}

/** The category the numpad should pre-select: whichever was used last. */
export function useLastUsedCategoryId(): string | null {
  const db = useContext(LedgerContext)?.db ?? personalDb;
  const recent = useLiveQuery(
    () =>
      db.expenses
        .orderBy("occurredAt")
        .reverse()
        .filter((e) => e.deletedAt === null)
        .limit(1)
        .toArray(),
    [db],
  );
  return recent?.[0]?.categoryId ?? null;
}

/**
 * The category Capture opens on: the default if it is live, else the last one
 * used, else the first (`startCategoryId`). Capture adopts this in the Capture
 * screen rebuild; until then it reads `useLastUsedCategoryId` directly.
 */
export function useStartCategoryId(): string | null {
  const live = useCategories();
  const lastUsedId = useLastUsedCategoryId();
  const { defaultCategoryId } = useProfile();
  return startCategoryId({ defaultId: defaultCategoryId, lastUsedId, live });
}

export function useSyncStatus(): FamilySyncStatus {
  const family = useContext(LedgerContext)?.kind === "family";
  const [status, setStatus] = useState<FamilySyncStatus>(family ? getFamilySyncStatus : getSyncStatus);
  useEffect(() => (family ? subscribeFamilySync : subscribeSync)(setStatus), [family]);
  return status;
}

/**
 * Rows written here but not yet accepted by the server, live from Dexie.
 *
 * Deliberately **not** read off `useSyncStatus().pending`. That figure only
 * advances when a sync runs, and a sync stops running entirely once the session
 * is gone (`sync()` returns at its own `readSession()` guard) — so during a dead
 * session, the very state where the count matters most, it would freeze at
 * whatever it happened to be when the token was cleared.
 */
export function usePendingCount(): number {
  const ledger = useContext(LedgerContext);
  return useLiveQuery(() => countPending(ledger ?? undefined), [ledger], 0);
}

export const NO_SPEND: Cents = ZERO;
